// Global Instructions Rule Applied!
//
// Agent Oversight — the period every history section reads over. Usage is
// counted in UTC days (agent_usage_daily.day and the caps' reset are both
// UTC), so the period is a pair of UTC calendar dates, inclusive, and the
// Activity list's timestamp range is derived from the same pair.

export type PeriodPreset = 'today' | '7d' | '30d' | '90d' | 'custom'

export interface Period {
  /** YYYY-MM-DD, UTC, inclusive. */
  date_from: string
  date_to: string
}

export const PERIOD_PRESETS: { value: PeriodPreset; label: string; days: number | null }[] = [
  { value: 'today', label: 'Today', days: 1 },
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '90d', label: '90 days', days: 90 },
  { value: 'custom', label: 'Custom', days: null }
]

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

/** Today's UTC date, YYYY-MM-DD. `now` is injectable for tests. */
export const utcToday = (now: Date = new Date()): string => isoDay(now)

/** The last `days` UTC days ending today (days = 1 → today only). */
export const presetPeriod = (days: number, now: Date = new Date()): Period => {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const from = new Date(to)
  from.setUTCDate(to.getUTCDate() - (Math.max(days, 1) - 1))
  return { date_from: isoDay(from), date_to: isoDay(to) }
}

/** The Activity list's timestamp bounds for a period: start of the first UTC
 *  day to the last millisecond of the last. */
export const periodTimestamps = (period: Period): { date_from: string; date_to: string } => ({
  date_from: `${period.date_from}T00:00:00.000Z`,
  date_to: `${period.date_to}T23:59:59.999Z`
})

/** Human label: "09/12/2026 – 09/18/2026 (UTC)" or a single day. */
export const periodLabel = (period: Period): string => {
  const us = (iso: string): string => `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}`
  return period.date_from === period.date_to
    ? `${us(period.date_from)} (UTC)`
    : `${us(period.date_from)} – ${us(period.date_to)} (UTC)`
}

/** Footer under a windowed table: "100 of 2,142 entries", or "13 entries"
 *  once the whole set is loaded; empty while nothing has loaded. */
export const windowLabel = (loaded: number, total: number, noun: string, nounPlural = `${noun}s`): string => {
  if (!total) return ''
  const plural = total === 1 ? noun : nounPlural
  return loaded < total
    ? `${loaded.toLocaleString()} of ${total.toLocaleString()} ${plural}`
    : `${total.toLocaleString()} ${plural}`
}
